import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, RefreshCw, Wrench, ImageOff, ListChecks, Boxes, FlaskConical, FileText } from "lucide-react";
import { adminHealth, adminHealthFix } from "@/lib/api";
import { cn } from "@/lib/utils";

const TYPE_META = {
  product: { label: "Produkto", icon: Boxes },
  science: { label: "Science", icon: FlaskConical },
  content: { label: "Nilalaman", icon: FileText },
};

const PROBLEM_META = {
  broken_media: { label: "Sirang media", cls: "bg-[hsl(0_60%_22%)] text-[hsl(0_85%_82%)]" },
  missing: { label: "Walang laman", cls: "bg-[hsl(38_60%_22%)] text-[hsl(38_90%_78%)]" },
};

const SummaryCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="rounded-xl border-border bg-card p-5" data-testid={testid}>
    <span className={cn("flex h-10 w-10 items-center justify-center rounded-lg", accent)}>
      <Icon className="h-5 w-5" />
    </span>
    <p className="mt-4 text-3xl font-semibold tracking-tight">{value}</p>
    <p className="mt-1 text-sm text-muted-foreground">{label}</p>
  </Card>
);

export const HealthManager = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminHealth();
      setReport(data);
    } catch (err) {
      toast.error("Hindi ma-load ang health report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runFix = async () => {
    setFixing(true);
    try {
      const data = await adminHealthFix();
      setReport(data);
      const fixed = data?.fixed ?? 0;
      if (fixed > 0) toast.success(`Naayos ang ${fixed} isyu. Na-resync ang mga media.`);
      else toast.success("Tapos ang re-sync. Walang natirang maaayos.");
    } catch (err) {
      toast.error("Hindi tumakbo ang auto-fix");
    } finally {
      setFixing(false);
    }
  };

  const summary = report?.summary;
  const issues = report?.issues || [];
  const healthy = summary?.healthy;

  return (
    <div className="space-y-6" data-testid="admin-health-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight">Data Health Check</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tinitignan kung may missing o sirang images sa live data bago pa magreklamo ang customer.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading || fixing} data-testid="admin-health-rescan-button">
            <RefreshCw className={cn("mr-2 h-4 w-4", loading && "animate-spin")} /> I-scan ulit
          </Button>
          <Button onClick={runFix} disabled={fixing || loading} data-testid="admin-health-fix-button">
            <Wrench className={cn("mr-2 h-4 w-4", fixing && "animate-spin")} /> {fixing ? "Inaayos..." : "Auto-fix / Re-sync"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="h-28 animate-pulse rounded-xl border-border bg-card" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              icon={healthy ? ShieldCheck : AlertTriangle}
              label={healthy ? "Malusog ang data" : "May kailangang ayusin"}
              value={healthy ? "OK" : (summary?.issues ?? 0)}
              accent={healthy ? "bg-[hsl(152_45%_20%)] text-[hsl(152_60%_78%)]" : "bg-[hsl(38_60%_22%)] text-[hsl(38_90%_78%)]"}
              testid="admin-health-status-card"
            />
            <SummaryCard icon={ImageOff} label="Sirang media" value={summary?.brokenMedia ?? 0} accent="bg-[hsl(0_55%_22%)] text-[hsl(0_85%_82%)]" testid="admin-health-broken-card" />
            <SummaryCard icon={AlertTriangle} label="Walang laman na field" value={summary?.missing ?? 0} accent="bg-[hsl(24_60%_24%)] text-[hsl(24_90%_78%)]" testid="admin-health-missing-card" />
            <SummaryCard icon={ListChecks} label="Na-check na image refs" value={summary?.mediaRefsChecked ?? 0} accent="bg-[hsl(198_55%_20%)] text-[hsl(198_70%_78%)]" testid="admin-health-checked-card" />
          </div>

          <Card className="rounded-xl border-border bg-card">
            {healthy ? (
              <div className="flex flex-col items-center gap-3 p-12 text-center" data-testid="admin-health-empty">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(152_45%_20%)] text-[hsl(152_60%_78%)]">
                  <ShieldCheck className="h-7 w-7" />
                </span>
                <p className="text-base font-semibold text-foreground">Walang problema — kumpleto ang lahat ng images!</p>
                <p className="max-w-md text-sm text-muted-foreground">
                  Nakumpirma na kompleto ang lahat ng product, science, at content images. Ligtas nang i-deploy.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Uri</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead>Problema</TableHead>
                      <TableHead>Reference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {issues.map((it, i) => {
                      const tmeta = TYPE_META[it.type] || { label: it.type, icon: FileText };
                      const pmeta = PROBLEM_META[it.problem] || { label: it.problem, cls: "bg-secondary text-foreground" };
                      const TIcon = tmeta.icon;
                      return (
                        <TableRow key={`${it.type}-${it.slug}-${it.field}-${i}`} data-testid="admin-health-issue-row">
                          <TableCell>
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                              <TIcon className="h-4 w-4 text-muted-foreground" /> {tmeta.label}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">
                            {it.name || it.slug}
                            {it.slug && it.name ? <div className="text-xs font-normal text-muted-foreground">{it.slug}</div> : null}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{it.field}</TableCell>
                          <TableCell>
                            <Badge className={cn("border-0 font-medium", pmeta.cls)}>{pmeta.label}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[260px] truncate text-xs text-muted-foreground">{it.ref || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
